import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ProductService } from 'src/app/service/product.service';
import { ToastrNotificationService } from 'src/app/service/toastr-notification.service';
import { Subscription, interval } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-create-master-batch-dialog',
  templateUrl: './create-master-batch-dialog.component.html',
  styleUrls: ['./create-master-batch-dialog.component.scss']
})
export class CreateMasterBatchDialogComponent implements OnInit, OnDestroy {
  batchForm!: FormGroup;
  isSubmitting: boolean = false;
  isPolling: boolean = false;
  generationProgress: number = 0;
  private pollSub?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<CreateMasterBatchDialogComponent>,
    private fb: FormBuilder,
    private productService: ProductService,
    private toastr: ToastrNotificationService
  ) {}

  ngOnInit(): void {
    this.batchForm = this.fb.group({
      startSeq: [1, [Validators.required, Validators.min(1)]],
      endSeq: [100, [Validators.required, Validators.min(1)]]
    }, { validators: this.rangeValidator });
  }

  ngOnDestroy(): void {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
    }
  }

  rangeValidator(group: AbstractControl): { [key: string]: boolean } | null {
    const start = group.get('startSeq')?.value;
    const end = group.get('endSeq')?.value;
    if (start !== null && end !== null && Number(end) < Number(start)) {
      return { invalidRange: true };
    }
    return null;
  }

  get calculatedTotal(): number {
    const start = Number(this.batchForm?.get('startSeq')?.value);
    const end = Number(this.batchForm?.get('endSeq')?.value);
    if (!isNaN(start) && !isNaN(end) && end >= start && start >= 1) {
      return end - start + 1;
    }
    return 0;
  }

  onCancel(): void {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
    }
    this.dialogRef.close(this.isPolling ? true : false);
  }

  onGenerate(): void {
    if (this.batchForm.invalid || this.calculatedTotal <= 0) {
      this.batchForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const payload = {
      startSeq: Number(this.batchForm.value.startSeq),
      endSeq: Number(this.batchForm.value.endSeq)
    };

    this.productService.createMasterBatch(payload).subscribe({
      next: (res) => {
        if (res.success) {
          const batchId = res.batch?._id;
          if (res.batch?.status === 'generating' && batchId) {
            this.isPolling = true;
            this.generationProgress = res.batch.progress || 0;
            this.startStatusPolling(batchId);
          } else {
            this.isSubmitting = false;
            this.toastr.success('Master batch generated successfully', 'Success');
            this.dialogRef.close(true);
          }
        } else {
          this.isSubmitting = false;
          this.toastr.error(res.error || 'Failed to generate master batch', 'Error');
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        if (err?.status !== 401) {
          this.toastr.error(err.error?.error || err.message || 'Error creating master batch', 'Error');
        }
      }
    });
  }

  private startStatusPolling(batchId: string): void {
    this.pollSub = interval(2000).pipe(
      switchMap(() => this.productService.getMasterBatchStatus(batchId))
    ).subscribe({
      next: (statusRes) => {
        if (statusRes.success && statusRes.batch) {
          const status = statusRes.batch.status;
          this.generationProgress = statusRes.batch.progress !== undefined ? statusRes.batch.progress : 0;

          if (status === 'completed') {
            this.cleanupAndClose(true, 'Master batch generated successfully');
          } else if (status === 'failed') {
            this.cleanupAndClose(false, 'Batch generation failed on server', true);
          }
        }
      },
      error: (err) => {
        console.error('Error polling batch status:', err);
      }
    });
  }

  private cleanupAndClose(success: boolean, message: string, isError: boolean = false): void {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
    }
    this.isSubmitting = false;
    this.isPolling = false;
    if (isError) {
      this.toastr.error(message, 'Error');
    } else {
      this.toastr.success(message, 'Success');
    }
    this.dialogRef.close(success);
  }
}
